import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useMutation } from "@tanstack/react-query";
import InnerLoading from "components/InnerLoading";
import { getWidth } from "components/MainContainer";
import {} from "constants/";
import {
  setActualHeldDetail,
  setIsChangingSeat,
  setKeptOriginalTicketIds,
  setOriginalEventId,
  setOriginalHeldDetail,
  setSelectedPartySize,
} from "features/slices/reservationFlowSlice";
import {
  BestTicketBlockDetails,
  PlanPackageComponentDetails,
  SeatDetails,
  SectionDetails,
  SubscriptionDetails,
  TenantSubdomain,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePreferredSections from "hooks/usePreferredSections";
import { ScheduleStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  fetchBestBlocksRequest,
  fetchPackageExchangeBestBlocksRequest,
  fetchPackageExchangeSectionsRequest,
  fetchSectionsRequest,
  holdForUpdateReservationRequest,
  holdTicketsRequest,
} from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Platform, View } from "react-native";
import { getRemainingSlotsForPlanPackage } from "screens/Reserve/helpers";
import Filters from "screens/SeatSelection/components/Filters";
import SectionList from "screens/SeatSelection/components/SectionList";
import {
  filterAccessible,
  filterChangingSeatsSectionRow,
  filterPremium,
  mergeBySectionRow,
  sortBlocksFunc,
} from "screens/SeatSelection/helpers";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
import toast from "utils/toast";
import EventNotices from "./components/EventNotices";
import SeatQuantitySelector from "./components/SeatQuantitySelector";
import VenueSeatMap from "./components/VenueSeatMap";
import { SeatSelectionState } from "./types";

export type SectionDetailsOrBestBlockDetailsArray =
  | SectionDetails[]
  | BestTicketBlockDetails[];

const componentSupportsProviderTemplate = (
  component: PlanPackageComponentDetails,
  providerTemplateId?: number,
) => {
  if (
    !providerTemplateId ||
    !Array.isArray(component.providerTemplateIds) ||
    component.providerTemplateIds.length === 0
  ) {
    return true;
  }
  return component.providerTemplateIds.includes(providerTemplateId);
};

// Calculates the remaining vouchers left for every voucher plan
function remainingVouchers(matchedSubs: SubscriptionDetails[]) {
  const remainingVouchers = matchedSubs.reduce(
    (sum, sub) => sum + sub.availableVouchers,
    0,
  );
  return remainingVouchers;
}

// Calculates the remaining reservations slots left for every reservation plan
function availableSlots(
  matchedSubs: SubscriptionDetails[],
  providerTemplateId?: number,
) {
  let subscriptionsMaxReservationQuantity = 0;
  let subscriptionsTotalTickets = 0;
  matchedSubs.forEach((sub) => {
    const primaryComponent =
      sub.planPackage.components.find(
        (component) =>
          component.primary &&
          componentSupportsProviderTemplate(component, providerTemplateId),
      ) ?? sub.planPackage.primaryComponent;
    const subReservationMaxQuantity = primaryComponent!.quantity;
    const subCurrentTickets = sub.currentTickets;
    subscriptionsMaxReservationQuantity += subReservationMaxQuantity;
    subscriptionsTotalTickets += subCurrentTickets;
  });
  return subscriptionsMaxReservationQuantity - subscriptionsTotalTickets;
}

const StandardSeatSelectionFlow = () => {
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { account } = useAccount();

  if (!tenantConfig || !account) return null;

  return <StandardSeatSelectionFlowContent />;
};

const StandardSeatSelectionFlowContent = () => {
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "SeatSelection">
    >();
  const dispatch = useAppDispatch();
  const {
    selectedEvent,
    selectedPartySize,
    selectedSubscription,
    isChangingSeats,
    originalTickets,
    originalEventId,
    keptOriginalTicketIds,
  } = useAppSelector((state) => state.reservationFlow);
  const { tenantSubdomain, tenantType, tenantThemeColor } = useAppSelector(
    (state) => state.tenant,
  );

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const maxSeatsAllowed = getMaxSeatsAllowedPerReservation(tenantConfig);
  const { account, accountEvents: events } = useAccount();
  const providerTemplateId = selectedEvent?.providerTemplateId;

  // Both "change seats" and "exchange" set isChangingSeats, but an exchange moves
  // the reservation to a different event (originalEventId is only set in that flow).
  // Current-seat display and add-on seat logic only apply within the same event.
  const isExchange =
    isChangingSeats &&
    originalEventId !== undefined &&
    originalEventId !== selectedEvent?.eventId;
  const isSameEventSeatChange = isChangingSeats && !isExchange;

  const usingStaticImage = useMemo(() => {
    const usingStaticSeatMapSubdomains: TenantSubdomain[] = [
      "thunderbirdcasino",
      "redwind",
    ];
    return usingStaticSeatMapSubdomains.includes(tenantSubdomain!);
  }, [tenantSubdomain]);

  const {
    preferredSections,
    setPreferredSections,
    handleAddPreferredSection,
    handleRemovePreferredSection,
  } = usePreferredSections();

  const tenantDefaultSortBy =
    tenantConfig?.defaultSeatSortBy ?? "PRICE_LOW_TO_HIGH";

  const [state, setState] = useState<SeatSelectionState>({
    originalPartySize: 0,
    // 0 based index indicates how many seat(s) will be holding, also v-modeled by v-chip-group.
    // fix https://github.com/FanRally/fanrally-core/issues/669
    selection: 0,
    step: 1,
    expansionPanel: false,
    selectedPlanType: "",
    sortBy: tenantDefaultSortBy,
    venueMapKey: 1,
    filters: {
      showPremium: true,
      showAccessible:
        account?.seatPreference === "INCLUDE_ACCESSIBLE" &&
        selectedEvent?.eventType !== "PARKING",
      sortBy: tenantDefaultSortBy,
    },
    allBlocks: [],
    mapKey: 1,
  });
  const [listHeight, setListHeight] = useState<number>(0);

  const planPackageForEvent = useMemo(() => {
    if (!selectedSubscription) return undefined;
    const filteredComponents =
      selectedSubscription.planPackage.components.filter((component) =>
        componentSupportsProviderTemplate(component, providerTemplateId),
      );
    const primaryComponentForEvent =
      filteredComponents.find((component) => component.primary) ||
      selectedSubscription.planPackage.primaryComponent;

    return {
      ...selectedSubscription.planPackage,
      components: filteredComponents,
      primaryComponent: primaryComponentForEvent,
    };
  }, [selectedSubscription, providerTemplateId]);

  const matchedSubs = useMemo(() => {
    if (!account || !selectedSubscription) return [];
    return account.subscriptions
      .filter(
        (sub) =>
          filterIneligibleSubscriptions(sub) &&
          sub.planPackage.id === selectedSubscription.planPackage.id,
      )
      .map((sub) => {
        const filteredComponents = sub.planPackage.components.filter(
          (component) =>
            componentSupportsProviderTemplate(component, providerTemplateId),
        );
        const primaryComponentForEvent =
          filteredComponents.find((component) => component.primary) ||
          sub.planPackage.primaryComponent;

        return {
          ...sub,
          planPackage: {
            ...sub.planPackage,
            components: filteredComponents,
            primaryComponent: primaryComponentForEvent,
          },
        };
      });
  }, [account, selectedSubscription, providerTemplateId]);

  const shouldShowGuestPrice =
    planPackageForEvent?.components.some((comp) =>
      comp.type.includes("GUEST"),
    ) || false;

  const {
    data: sections,
    isLoading: fetchingSections,
    mutateAsync: fetchSections,
  } = useMutation(fetchSectionsRequest);
  const {
    data: packageExchangeSections,
    mutateAsync: fetchPackageExchangeSections,
    isLoading: fetchingPackageExchangeSections,
  } = useMutation(fetchPackageExchangeSectionsRequest);

  const {
    data: bestBlocks,
    mutateAsync: fetchBestBlocks,
    isLoading: fetchingBlocks,
  } = useMutation(fetchBestBlocksRequest);

  const {
    data: packageExchangeBestBlocks,
    mutateAsync: fetchPackageExchangeBestBlocks,
    isLoading: fetchingPackageExchangeBlocks,
  } = useMutation(fetchPackageExchangeBestBlocksRequest);

  // For TDC package exchange
  const originalTdcPackageTicket = originalTickets.find(
    (t) => t.tdcPackageId !== null,
  );
  const tdcPackageId = originalTdcPackageTicket?.tdcPackageId;
  const tdcPackageListId = originalTdcPackageTicket?.tdcPackageListId;
  const tdcBuyerTypeId = originalTdcPackageTicket?.tdcBuyerTypeId;

  const handleFetchBestBlocks = (partySize: number) => {
    if (originalTdcPackageTicket) {
      return fetchPackageExchangeBestBlocks({
        eventId: selectedEvent?.eventId,
        planPackageId: selectedSubscription?.planPackage.id,
        partySize,
        sections: preferredSections,
        premiumOnly: false,
        tdcPackageId,
        tdcPackageListId,
        tdcBuyerTypeId,
      });
    }
    return fetchBestBlocks({
      eventId: selectedEvent?.eventId,
      planPackageId: selectedSubscription?.planPackage.id,
      partySize,
      sections: preferredSections,
      premiumOnly: false,
      // When updating a same-event reservation, the backend uses the group code to let the
      // user's current seats bridge otherwise-too-small available runs in their row
      groupCode: isSameEventSeatChange
        ? originalTickets[0]?.groupCode ?? undefined
        : undefined,
    });
  };

  const handleFetchSections = (partySize: number) => {
    if (originalTdcPackageTicket) {
      return fetchPackageExchangeSections({
        eventId: selectedEvent?.eventId,
        planPackageId: selectedSubscription?.planPackage.id,
        partySize,
        tdcPackageId,
        tdcPackageListId,
        tdcBuyerTypeId,
      });
    }
    return fetchSections({
      eventId: selectedEvent?.eventId,
      planPackageId: selectedSubscription?.planPackage.id,
      partySize,
    });
  };

  const { mutateAsync: holdTickets, isLoading: holdingTickets } =
    useMutation(holdTicketsRequest);
  const {
    mutateAsync: holdForUpdateReservation,
    isLoading: holdingUpdateTickets,
  } = useMutation(holdForUpdateReservationRequest);

  const isTicketProviderCaptured = selectedEvent?.ticketProvider === "CAPTURED";

  const checkIfBlocksContainAccessible = (
    blocks: BestTicketBlockDetails[] | SectionDetails[],
  ) => {
    if (!blocks) return;
    const containAccessible = blocks.some((block) => block.accessible);
    // if (isTicketProviderCaptured) return;
    if (state.filters.showAccessible && !containAccessible) {
      const { supportEmail, supportPhone } = tenantConfig!;
      let msg = i18n.t("screens.SeatSelection.preferredSeatNotAvailable", {
        sectionOrGame: selectingBestBlock
          ? i18n.t("screens.SeatSelection.section")
          : i18n.t("screens.SeatSelection.game"),
      });
      if (supportEmail && supportPhone) {
        msg += `call ${supportPhone} or email ${supportEmail}`; // if tenant has both support email and support phone
      } else if (supportEmail) {
        // if tenant has only support email
        msg += `email ${supportEmail}`;
      } else {
        msg += `call ${supportPhone}`;
      }
      toast.warning(msg);
    }
  };

  const filteredSections = useMemo(() => {
    const filteredResult = (sections || packageExchangeSections || [])
      .filter((block) => block.premiumClass === null) // filter out any premium class sections
      .filter((block) => filterAccessible(block, state.filters.showAccessible))
      .filter((block) => filterPremium(block, state.filters.showPremium)) // the show premium flag means if a price is associated with the section
      .filter((block) =>
        filterChangingSeatsSectionRow(
          block,
          isSameEventSeatChange,
          originalTickets,
        ),
      )
      .sort((a, b) => sortBlocksFunc(a, b, state.filters.sortBy));

    return filteredResult;
  }, [
    sections,
    packageExchangeSections,
    state.filters,
    isSameEventSeatChange,
    originalTickets,
  ]);

  const filteredBlocks = useMemo(() => {
    let sortedBestBlocks =
      (Boolean(bestBlocks?.length) ? bestBlocks : packageExchangeBestBlocks) ||
      [];

    sortedBestBlocks = sortedBestBlocks
      .filter((block) => filterAccessible(block, state.filters.showAccessible))
      .filter((block) => filterPremium(block, state.filters.showPremium))
      .sort((a, b) => sortBlocksFunc(a, b, state.filters.sortBy));

    if (isTicketProviderCaptured && preferredSections.length > 0) {
      sortedBestBlocks = sortedBestBlocks.filter((block) =>
        preferredSections.includes(block.section),
      );
    }
    // Combine blocks that share a section + row so each row renders once,
    // even when its availability comes back as multiple seat blocks
    return mergeBySectionRow(sortedBestBlocks);
  }, [bestBlocks, packageExchangeBestBlocks, preferredSections, state.filters]);

  const handleZeroPartySize = (
    selectedPlanType: PlanPackageComponentDetails["type"],
    matchedSubs: SubscriptionDetails[],
    providerTemplateId?: number,
  ) => {
    // can't reserve or redeem tickets for this event
    const maximumTicketMsg = i18n.t("screens.SeatSelection.maximumTicketMsg");
    const noReservation = i18n.t("screens.SeatSelection.noReservation");
    const noVoucher = i18n.t("screens.SeatSelection.noVoucher");
    const generateMsg = (msg: string) => {
      return i18n.t("screens.SeatSelection.noTicketMsg", { msg });
    };
    let msg = "";
    switch (selectedPlanType) {
      case "VOUCHER":
        if (remainingVouchers(matchedSubs) === 0) {
          // out of vouchers

          msg = generateMsg(noVoucher);
        } else {
          msg = maximumTicketMsg;
        }
        break;
      case "BANK":
      case "RESERVATION":
        if (availableSlots(matchedSubs, providerTemplateId) === 0) {
          // out of reservation slots
          msg = generateMsg(noReservation);
        } else {
          msg = maximumTicketMsg;
        }
        break;
    }
    toast.warning(msg);
  };

  const selectingBestBlock = isTicketProviderCaptured || state.step === 2;

  const perReservationMinimum = useMemo(() => {
    const plan = planPackageForEvent ?? selectedSubscription?.planPackage;

    if (!plan) return 1;

    const enabledComponents =
      plan.components?.filter((component) => !component.disabled) ?? [];

    const minimum = enabledComponents.reduce(
      (total, component) => total + (component.perGameRedemptionMinimum ?? 0),
      0,
    );

    return Math.max(minimum, 1);
  }, [planPackageForEvent, selectedSubscription]);

  const minPartySize = useMemo(() => {
    // If this is a package exchange, the minimum party size should be the amount of package tickets.
    if (originalTdcPackageTicket) {
      return originalTickets.filter((t) => t.tdcPackageId !== null).length;
    }
    return perReservationMinimum;
  }, [originalTdcPackageTicket, originalTickets, perReservationMinimum]);

  const handleSelectedPartySizeChange = (partySize: number) => {
    dispatch(setSelectedPartySize(partySize));
    setState((state) => ({
      ...state,
      step: 1,
    }));
    setPreferredSections([]);
  };

  const handleBackBtnPress = async () => {
    if (Platform.OS === "web") {
      setState((state) => ({
        ...state,
        step: 1,
        mapKey: state.mapKey + 1,
      }));
    }
    setPreferredSections([]);
  };

  const handleSectionPress = (section: SectionDetails) => {
    setPreferredSections([section.section]);
  };

  const handleBestBlockPress = async (seats: SeatDetails[]) => {
    // GA detection: seats may be empty for GA blocks (no specific seat pre-selected),
    // so fall back to finding any GA block in the filtered list when seats[0] is absent.
    const sectionName = seats[0]?.section;
    const selectedBlock = filteredBlocks.find(
      (block) => block.section === sectionName,
    );
    const isGA = selectedBlock?.ga ?? false;

    let seatsToUse = seats;

    if (isGA) {
      // For GA sections the caller may not pass specific seat IDs, so we collect
      // every available GA seat from filteredBlocks and slice to the requested party
      // size. This also ensures we never re-send an already-held original seat as a
      // "new" selection — doing so causes the backend to count Selected=0 and throw
      // PER_GAME_REDEMPTION_MINIMUM_NOT_MET.
      const allAvailableSeats: SeatDetails[] = [];
      filteredBlocks.forEach((block) => {
        if (block.ga && block.tickets && Array.isArray(block.tickets)) {
          allAvailableSeats.push(
            ...(block.tickets as unknown as SeatDetails[]),
          );
        }
      });

      // When changing seats within the same event, exclude original seat IDs so we
      // only hold genuinely new seats. GA seat IDs are interchangeable, so any
      // non-original seat works. On an exchange the original seats belong to a
      // different event, so they're legitimately available here.
      const originalSeatIdSet = new Set(originalTickets.map((t) => t.seat.id));
      const candidateSeats = isSameEventSeatChange
        ? allAvailableSeats.filter((s) => !originalSeatIdSet.has(s.id))
        : allAvailableSeats;

      seatsToUse = candidateSeats.slice(0, selectedPartySize);
    }

    if (seatsToUse.length === 0) {
      toast.warning(i18n.t("screens.SeatSelection.noSeats"));
      return;
    }

    dispatch(
      setOriginalHeldDetail({
        section: seatsToUse[0].section,
        row: seatsToUse[0].row,
      }),
    );
    if (!selectedSubscription || !selectedEvent) return;

    let response: TicketDetails[] = [];

    if (isChangingSeats) {
      // For GA sections, seat IDs are interchangeable — all original GA tickets are
      // treated as released and entirely new ones are held. On an exchange the
      // original tickets are for a different event, so nothing can be "kept" and
      // every selected seat is new. For same-event non-GA changes, check which
      // specific original seats the user is keeping.
      const originalSeatIdSet = new Set(originalTickets.map((t) => t.seat.id));
      const keptOriginalTickets =
        isGA || isExchange
          ? []
          : originalTickets.filter((t) =>
              seatsToUse.some((s) => s.id === t.seat.id),
            );
      // For GA, seatsToUse was already filtered above to exclude original seats.
      const newSeats =
        isGA || isExchange
          ? seatsToUse
          : seatsToUse.filter((s) => !originalSeatIdSet.has(s.id));

      dispatch(setKeptOriginalTicketIds(keptOriginalTickets.map((t) => t.id)));

      // If the user made no changes (kept all their original seats, nothing new), don't proceed
      if (newSeats.length === 0) {
        toast.info("Your current seats have been kept. No changes made.");
        return;
      }

      // The backend needs the release intent at hold time: for TDC package
      // tickets the exchange (or plain add-on hold) is committed against the
      // provider when the seats are held, not when the reservation is finalized.
      const keptOriginalIdSet = new Set(keptOriginalTickets.map((t) => t.id));
      const releaseTicketIds = originalTickets
        .filter((t) => !keptOriginalIdSet.has(t.id))
        .map((t) => t.id);

      response = await holdForUpdateReservation({
        accountId: account!.id,
        planPackageId: selectedSubscription!.planPackage.id,
        eventId: selectedEvent!.eventId,
        groupCode: originalTickets[0].groupCode!,
        selections: newSeats,
        premiumOnly: false,
        releaseTicketIds,
      });
    } else {
      response = await holdTickets({
        planPackageId: selectedSubscription.planPackage.id,
        eventId: selectedEvent.eventId,
        partySize: selectedPartySize,
        selections: seatsToUse,
        premiumOnly: false,
      });
    }

    dispatch(setActualHeldDetail(response));
    navigation.push("Reserve");
  };

  useEffect(() => {
    async function initSeatData() {
      if (!selectedEvent) return;
      // Reset any stale keptOriginalTicketIds from a previous flow before calculating
      // available slots — at page load we don't know which originals will be kept yet.
      dispatch(setKeptOriginalTicketIds([]));
      const {
        selectedPlanType,
        partySize,
        defaultSelectedPartySize,
        partySizePlusGuest,
      } = getRemainingSlotsForPlanPackage({
        subscriptions: matchedSubs,
        planPackage: planPackageForEvent || selectedSubscription!.planPackage,
        eventId: selectedEvent!.eventId,
        events,
        isChangingSeats,
        originalEventId,
        maxSeatsAllowed,
        groupCode: isChangingSeats
          ? (originalTickets[0]?.groupCode ?? undefined)
          : undefined,
        keptOriginalTicketIds: [], // always empty at page load; set later in handleBestBlockPress
      });
      // run out of reservation slots for an event, excluding guests
      if (partySize <= 0) {
        handleZeroPartySize(selectedPlanType, matchedSubs, providerTemplateId);
        return;
      }

      // https://github.com/FanRally/fanrally-core/issues/1281 bullet point No.3
      const finalPartySize = isChangingSeats
        ? originalTickets.length
        : defaultSelectedPartySize;

      const maxBetweenPerGameRedemptionMinimumAndPartySize = Math.max(
        finalPartySize,
        perReservationMinimum,
      );

      dispatch(
        setSelectedPartySize(
          Math.min(
            maxBetweenPerGameRedemptionMinimumAndPartySize,
            maxSeatsAllowed,
          ),
        ),
      );
      setState((state) => ({
        ...state,
        originalPartySize: Math.min(partySizePlusGuest, maxSeatsAllowed),
        // https://github.com/FanRally/fanrally-core/issues/862
        selectedPlanType,
      }));
    }

    initSeatData();
  }, [selectedEvent]);

  useEffect(() => {
    async function fetchAllBlocks() {
      if (selectedPartySize === 0) return;
      let allBlocks: BestTicketBlockDetails[] | SectionDetails[] = [];
      if (isTicketProviderCaptured) {
        allBlocks = await handleFetchBestBlocks(selectedPartySize);
      } else {
        allBlocks = await handleFetchSections(selectedPartySize);
      }
      checkIfBlocksContainAccessible(allBlocks);
      setState((state) => ({
        ...state,
        allBlocks,
      }));
    }
    fetchAllBlocks();
  }, [selectedPartySize]);

  useEffect(() => {
    if (!sections && !bestBlocks) return; // this means page is just started loading

    if (selectingBestBlock) {
      checkIfBlocksContainAccessible(filteredBlocks);
    } else {
      checkIfBlocksContainAccessible(filteredSections);
    }
  }, [state.filters.showAccessible]);

  useEffect(() => {
    async function handlePreferredSectionsChange() {
      if (!isTicketProviderCaptured) {
        if (preferredSections.length === 0) {
          // Return the same reference when step is already 1 so React bails out
          setState((prev) => (prev.step !== 1 ? { ...prev, step: 1 } : prev));
        } else {
          setState((state) => ({ ...state, step: 2 }));
          const blocks = await handleFetchBestBlocks(selectedPartySize);
          checkIfBlocksContainAccessible(blocks);
        }
      }
    }
    handlePreferredSectionsChange();
  }, [preferredSections, state.allBlocks]);

  useEffect(() => {
    // when leaving page, reset reservation flow state
    return () => {
      dispatch(setIsChangingSeat(false));
      dispatch(setOriginalEventId(undefined));
    };
  }, []);
  const onLayout = (event: any) => {
    const { height } = event.nativeEvent.layout;
    setListHeight(height);
  };

  function removePremium(blocks: SectionDetailsOrBestBlockDetailsArray) {
    return blocks.filter((block) => block.premiumClass === null);
  }

  // Safety guard — wrapper component already ensures these are loaded,
  // but this narrows types for TypeScript.
  if (!tenantConfig || !account) return null;

  const isLoading =
    fetchingSections ||
    fetchingBlocks ||
    fetchingPackageExchangeSections ||
    fetchingPackageExchangeBlocks ||
    holdingTickets ||
    holdingUpdateTickets;

  return (
    <View className="relative" style={{ height: "100%" }}>
      {selectedEvent && (
        <EventNotices eventGroupNotices={selectedEvent.eventGroupNotices} />
      )}
      <View
        style={{
          flex: 1,
          width: getWidth(),
          alignSelf: "center",
          height: "100%",
        }}
      >
        <View style={{ flex: 0.4, paddingTop: 5 }}>
          <VenueSeatMap
            mapId="seat-selection"
            usingStaticImage={usingStaticImage}
            tenantType={tenantType!}
            tenantSubdomain={tenantSubdomain!}
            theme={tenantThemeColor}
            eventType={selectedEvent!.eventType}
            venueName={selectedEvent!.venueName}
            selectedPartySize={selectedPartySize}
            blocks={
              removePremium(
                state.allBlocks,
              ) as SectionDetailsOrBestBlockDetailsArray
            }
            seatGroups={
              planPackageForEvent?.primaryComponent.seatGroups ||
              selectedSubscription!.planPackage.primaryComponent.seatGroups
            }
            onSectionAdded={handleAddPreferredSection}
            onSectionRemoved={handleRemovePreferredSection}
            venueMapKey={state.mapKey}
            onReset={() => setPreferredSections([])}
            preferredSections={preferredSections}
            includePremium={state.filters.showPremium}
          />
        </View>
        <View
          style={{ flex: 0.08, flexDirection: "row", alignItems: "center" }}
        >
          <SeatQuantitySelector
            size={state.originalPartySize}
            selectedPartySize={selectedPartySize}
            onChange={handleSelectedPartySizeChange}
            minPartySize={minPartySize}
            eventType={selectedEvent!.eventType}
          />
          <View
            style={{
              paddingHorizontal: 10,
              position: "relative",
            }}
          >
            <Filters
              filters={state.filters}
              onApply={(newFilterValues) =>
                setState((state) => ({ ...state, filters: newFilterValues }))
              }
              bestBlocks={filteredBlocks}
            />
          </View>
        </View>

        <View
          style={{
            flex: 0.45,
            opacity: state.originalPartySize === 0 ? 0.3 : 1,
          }}
          onLayout={onLayout}
        >
          {isLoading ? (
            <InnerLoading />
          ) : (
            <View>
              {selectedEvent && (
                <SectionList
                  sections={filteredSections}
                  bestBlocks={filteredBlocks}
                  selectingBestBlock={selectingBestBlock}
                  height={listHeight}
                  handleSectionPress={handleSectionPress}
                  handleBestBlockPress={handleBestBlockPress}
                  onBackBtnPress={handleBackBtnPress}
                  shouldShowGuestPrice={shouldShowGuestPrice}
                  ticketProvider={selectedEvent.ticketProvider}
                  seatSelectionStep={state.step}
                  allBlocks={bestBlocks || []}
                  filters={state.filters}
                  originalTickets={
                    isSameEventSeatChange ? originalTickets : undefined
                  }
                />
              )}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default StandardSeatSelectionFlow;
